// NotesApp source file : data.service.ts
// 🔧 COMPATIBLE DROP-IN FIXED VERSION

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  defer,
  firstValueFrom,
  of,
  from,
  throwError,
  Subject,
} from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

import { DataRemoteService } from './data-remote.service';
import { DataOfflineService } from './data-offline.service';
import { EnvironmentService } from './sync/environment.service';
import { DialogType, GlobalDialogService } from '../_service/global-dialog.service';
import { LogService } from '../_service/log.service';
import { SnackbarService } from './snackbar.service';
import Delta from 'quill-delta';
import { appendAiIndexActivity } from '../_util/ai-index-activity.util';
//import { SyncCoordinatorService } from './sync/sync-coordinator.service';


/* ============================================================
   PUBLIC INTERFACES — MUST REMAIN EXPORTED
   ============================================================ */

export interface AiSummary {
  summary_status: 'pending' | 'stale' | 'current' | 'failed' | 'disabled' | string;
  summary_stale?: boolean;
  summary_requested_at?: string;
  summary_generated_at?: string;
  summary_content_hash?: string;
  summary_note_revision?: string;
  summary_model?: string;
  summary_error?: string;
  summary_token_input?: number;
  summary_token_output?: number;
  summary_one_sentence?: string;
  summary_paragraph?: string;
  summary_bullets?: string[];
  summary_keywords?: string[];
  summary_for_embedding?: string;
}

export interface AiSummaryRegenerateResult {
  note_id: string;
  queued: boolean;
  source_hash?: string;
}

export interface AiSummaryStatusResult {
  note_id: string;
  summary_status: 'none' | 'pending' | 'stale' | 'current' | 'failed' | string;
  summary_generated_at: string | null;
  summary_model: string | null;
  summary_error: string | null;
  summary?: {
    one_sentence: string;
    paragraph: string;
    bullets: string[];
    keywords: string[];
  };
}

export interface Note {
  _id: string;
  _rev?: string;
  type: string;
  templateName?: string;
  NoteType: string;
  Title: string;
  Project: number;
  ProjectName: string;
  Status: string;
  ReferenceDate?: string;
  StartDate?: string;
  EndDate?: string;
  Confidential?: boolean;
  DescriptionDelta: Delta;
  CommentsDelta: Delta;
  DescriptionHtml: string;
  CommentsHtml: string;
  Keywords: string;
  AiKeywords: string;
  created_on: string;
  last_modified: string;
  Categories: string[];
  Size?: number;
  Tags?: Array<string | Tag>;
  Attachments?: string[];
  RelatedNotes?: Array<string | RelatedNoteEntry>; // legacy bare-id entries are tolerated on read
  ai_summary?: AiSummary | null;
  remote_rev?: string;
  syncStatus?: string;
  syncTime?: string;
}

/** The subset of a note's editable fields captured in an opening snapshot —
 * mirrors exactly what applyChangesToNoteFromForm() reads/writes, so a
 * snapshot's content is always structurally comparable to (and restorable
 * into) a live note. Deliberately excludes Attachments (binaries aren't
 * duplicated) and identity/sync fields (_id, _rev, syncStatus, etc). */
export interface NoteEditSessionSnapshotContent {
  Title: string;
  DescriptionHtml: string;
  DescriptionDelta: Delta;
  CommentsHtml: string;
  CommentsDelta: Delta;
  Keywords: string;
  AiKeywords: string;
  Status: string;
  NoteType: string;
  Confidential?: boolean;
  ReferenceDate?: string;
  StartDate?: string;
  EndDate?: string;
  Categories: string[];
  Tags: Array<string | Tag>;
  RelatedNotes: Array<string | RelatedNoteEntry>;
}

/** Autosave Stage 2 — an IndexedDB-only "opening snapshot" of a note as it
 * stood when an edit session began, used to power "Discard changes since
 * opening…". Never synchronized to the backend. Keyed by (noteId, sessionId)
 * so multiple tabs/sessions editing the same note over time don't collide. */
export interface NoteEditSessionSnapshot {
  noteId: string;
  sessionId: string;
  openedAt: string;
  lastActiveAt: string;
  openingRev?: string;
  instanceId: string;
  status: 'active' | 'closed';
  lockRef?: string;
  content: NoteEditSessionSnapshotContent;
}

export interface SimilarNoteResult {
  _id?: string;
  id?: string;
  title?: string;
  Status?: string;
  updated_at?: string;
  modified_at?: string;
  modifiedDate?: string;
  last_modified?: string;
  final_score?: number;
  semantic_score?: number;
  recency_boost?: number;
  status_boost?: number;
  [key: string]: unknown;
}

export interface SimilarNotesResponse {
  results?: SimilarNoteResult[];
  [key: string]: unknown;
}

/** An entry in a note's RelatedNotes list: the linked note's id, plus its
 * explicit display order relative to the other related notes. A null
 * sortOrder sorts last — see related-notes.util.ts. */
export interface RelatedNoteEntry {
  /** ID of the referenced note (e.g. "note:abc123"). */
  id: string;
  /** Explicit display position; null sorts last. Always explicit after a save. */
  sortOrder: number | null;
}

export interface SuggestedTagResult {
  _id: string;
  name: string;
  Description?: string;
}

export interface SuggestTagsResponse {
  suggested_tags: SuggestedTagResult[];
  count: number;
}

export interface NoteType {
  ID: number;
  Name: string;
}

export interface Project {
  ID: number;
  Name: string;
}

export interface Status {
  Status: string;
}

export interface Category {
  _id: string;
  Name: string;

  // hierarchy
  Parent: string | null;
  LeftValue: number;
  RightValue: number;
  Path: string;

  // OPTIONAL BUT REQUIRED BY UI
  ShortName?: string;
  Color?: string;
  Icon?: string;
  Owner?: string;
  SortOrder?: number;
}

export interface Tag {
  _id: string;
  _rev?: string;
  type: 'tag';
  name: string;
  description?: string;
  tagType: string;
  slug: string;
  color?: string;
  icon?: string;
  parentTagId: string | null;
  sortOrder?: number;
  isActive: boolean;
}

export interface TagMutationPayload {
  name: string;
  description?: string;
  tagType: string;
  slug: string;
  color?: string;
  icon?: string;
  parentTagId?: string | null;
  sortOrder?: number;
  isActive: boolean;
}

export interface BulkCategoryResult {
  updatedCount?: number;
  updated?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

export interface BulkStatusResult {
  updatedCount?: number;
  updated?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

export interface BulkDeleteResult {
  updatedCount?: number;
  deletedCount?: number;
  deleted?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

export interface BulkTagAddResult {
  updatedCount?: number;
  updated?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

export interface BulkTagRemoveResult {
  updatedCount?: number;
  updated?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

export interface ApiResponse<T> {
  status: 'success' | 'error' | 'warning';
  data?: T;
  error?: { code: number; message: string };
}

export interface BulkCategoryResult {
  updatedCount?: number;
  message?: string;
}

export interface BulkStatusResult {
  updatedCount?: number;
  message?: string;
}

export interface BulkDeleteResult {
  updatedCount?: number;
  updated?: number;        // ✅ add this so union access works
  deletedCount?: number;
  deleted?: number;
  count?: number;
  failedIds?: string[];
  failed?: string[];
  message?: string;
}

export interface DbInfo {
  db_name: string;
  total: number;
  active: number;
  deleted_status: number;
  tombstones: number;
  update_seq: string | number;
  generated_at: string;
  status_field_present?: boolean; // ✅ add this (your offline service writes it)
}


/* ============================================================
   DATA SERVICE
   ============================================================ */

@Injectable({ providedIn: 'root' })
export class DataService {
  private readonly baseUrl = environment.apiBaseUrl || '/api';
  public isOnline = navigator.onLine;

  /** Canonical in-memory state */
  public allNotes: Note[] = [];
  public allCategories: Category[] = [];
  public allTags: Tag[] = [];
  public allTemplates: Note[] = [];

  private dataLoadedSubject = new BehaviorSubject<boolean>(false);
  public dataLoaded$ = this.dataLoadedSubject.asObservable();
  private dataReloadedSubject = new BehaviorSubject<Date | null>(null);
  public dataReloaded$ = this.dataReloadedSubject.asObservable();
  private templatesLoadedSubject = new BehaviorSubject<boolean>(false);
  public templatesLoaded$ = this.templatesLoadedSubject.asObservable();

  private isSyncing = false;

  private upsertNoteInMemory(note: Note): void {
    if (!note || !note._id) {
      return;
    }

    const isTemplate = note.type === 'template' || String(note._id).startsWith('template:');
    if (isTemplate) {
      const templateIndex = this.allTemplates.findIndex((item) => item._id === note._id);
      if (templateIndex >= 0) {
        this.allTemplates[templateIndex] = { ...this.allTemplates[templateIndex], ...note };
      } else {
        this.allTemplates = [...this.allTemplates, note];
      }
      return;
    }

    const noteIndex = this.allNotes.findIndex((item) => item._id === note._id);
    if (noteIndex >= 0) {
      this.allNotes[noteIndex] = { ...this.allNotes[noteIndex], ...note };
    } else {
      this.allNotes = [...this.allNotes, note];
    }
  }

  private emitLocalDataChanged(): void {
    this.dataLoadedSubject.next(true);
    this.dataReloadedSubject.next(new Date());
  }

  private triggerNoteIndexRefresh(note: Note): void {
    if (!this.isOnline || !note?._id || note.type !== 'note') {
      return;
    }

    const noteId = encodeURIComponent(note._id);
    const reindexUrl = `${this.baseUrl}/ai/reindex-note/${noteId}`;
    const legacyReindexUrl = `${this.baseUrl}/rag/reindex/${noteId}`;

    this.http
      .post<ApiResponse<{ message?: string }>>(reindexUrl, {})
      .pipe(
        catchError((err) => {
          const status = Number((err as any)?.status ?? 0);
          // Backward compatibility: if the new endpoint is unavailable, try the legacy endpoint.
          if (status === 404 || status === 405) {
            return this.http.post<ApiResponse<{ message?: string }>>(legacyReindexUrl, null);
          }
          return throwError(() => err);
        }),
      )
      .subscribe({
      next: (res) => {
        appendAiIndexActivity({
          kind: 'note_reindex',
          timestamp: new Date().toISOString(),
          status: 'success',
          source: 'note_save',
          noteId: note._id,
          noteTitle: note.Title,
          details: String(res?.data?.message ?? 'Note index refresh requested.').trim(),
        });
      },
      error: (err) => {
        const errorMessage =
          String(err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? 'Note index refresh failed.')
            .trim();
        appendAiIndexActivity({
          kind: 'note_reindex',
          timestamp: new Date().toISOString(),
          status: 'failed',
          source: 'note_save',
          noteId: note._id,
          noteTitle: note.Title,
          error: errorMessage,
        });
      },
    });
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private dataRemoteService: DataRemoteService,
    private dataOfflineService: DataOfflineService,
    private environmentService: EnvironmentService,
    private gDS: GlobalDialogService,
    private logs: LogService,
    private snackbarService: SnackbarService,
    //private readonly syncCoordinator: SyncCoordinatorService,
  ) {
    this.environmentService.online$.subscribe((online) => {
      this.isOnline = online;
      this.onOnlineStatusChange(online);
    });

    // 🔥 FIX: baseline completion reloads IndexedDB into memory
    this.dataOfflineService.onBaselineCompleted().subscribe(async () => {
      this.logs.append('synch', '[DataService] baseline completed → reload');
      await this.reloadFromIndexedDB();
    });

    this.dataOfflineService.onIncrementalChangesApplied().subscribe(async () => {
      this.logs.append('synch', '[DataService] incremental changes applied → reload');
      await this.reloadFromIndexedDB();
    });
  }


  async initialize(): Promise<void> {
    await this.reloadFromIndexedDB();
    // Load templates at startup for instant access
    this.loadTemplatesIntoCache();
  }


    // ✅ Single public sync entry point
//   syncAllChanges(
//     reason: 'manual' | 'online' | 'debug' = 'manual',
//   ): Promise<boolean> {
//     return this.syncCoordinator.forceSync(reason);
//   }

// src/app/_service/data.service.ts

onOnlineStatusChange(isOnline: boolean): void {
  if (!isOnline) {
    console.log('[DataService] App is offline');
    return;
  }

  console.log('[DataService] App is back online → requesting sync');
  //void this.syncAllChanges('online');
}

fetchRemoteDbInfoAndLogActive(): void {
  this.dataRemoteService.fetchDbInfoAndLogActive();
}

fetchOfflineDbInfoAndLogActive(): void {
  this.dataOfflineService.fetchDbInfoAndLogActive();
}

async clearOfflineStorage(): Promise<void> {
  await this.dataOfflineService.clearOfflineAndLocal();
  this.allNotes = [];
  this.allCategories = [];
  this.dataLoadedSubject.next(false);
  this.logs.append('synch', '[DataService] Offline storage cleared');
}


  /* ============================================================
     🔥 FIXED: INITIALIZE (startup.service depends on this)
     ============================================================ */

  async refreshLocalData(): Promise<void> {
    await this.reloadFromIndexedDB();
  }

  /* ============================================================
     🔥 FIXED: reloadFromIndexedDB
     ============================================================ */

  private async reloadFromIndexedDB(): Promise<void> {
    //this.logs.append('synch', '[DataService] reloadFromIndexedDB()');

    const notes = await this.dataOfflineService.getAllNotes();
    const categories = await this.dataOfflineService.getAllCategories();

    this.allNotes = notes.filter((n) => n.type === 'note');
    this.allCategories = categories ?? [];

    // 🔥 Per-note logging (your explicit requirement)
    // for (const n of this.allNotes) {
    //   this.logs.append(
    //     'synch',
    //     `[LOAD] ${n._id} — ${n.Title || '(no title)'}`
    //   );
    // }

    // this.logs.append(
    //   'synch',
    //   `[DataService] Loaded ${this.allNotes.length} notes, ${this.allCategories.length} categories`,
    // );

    this.dataLoadedSubject.next(true);
    this.dataReloadedSubject.next(new Date());
  }

  /* ============================================================
     READ OPERATIONS (unchanged semantics)
     ============================================================ */

  getAllNotes(): Observable<ApiResponse<Note[]>> {
    return defer(() => from(this.dataOfflineService.getAllNotes())).pipe(
      map((notes) => ({
        status: 'success' as const,
        data: notes.filter((n) => n.type === 'note'),
      })),
      catchError((err) =>
        of({
          status: 'error' as const,
          error: { code: 0, message: String(err) },
        }),
      ),
    );
  }

  getAllCategories(): Observable<ApiResponse<Category[]>> {
    return defer(() => from(this.dataOfflineService.getAllCategories())).pipe(
      map((categories) => ({
        status: 'success' as const,
        data: categories ?? [],
      })),
      tap((resp) => {
        if (resp.status === 'success') {
          console.log('[DataService] getAllCategories: loaded', resp.data?.length ?? 0, 'categories from IndexedDB');
          if (resp.data && resp.data.length > 0) {
            console.log('[DataService] getAllCategories: sample category', resp.data[0]);
          }
        }
      }),
      catchError((err) => {
        console.error('[DataService] getAllCategories: failed to load from IndexedDB', err);
        return of({
          status: 'error' as const,
          error: { code: 0, message: String(err) },
        });
      }),
    );
  }

  getAllTags(): Observable<ApiResponse<Tag[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/tags`).pipe(
      map((resp) => {
        const tags = Array.isArray(resp.data) ? resp.data.map((tag) => this.normalizeTagFromApi(tag)) : [];
        this.allTags = tags;
        // Offline-first: cache the canonical tag list (with real names) locally so it
        // remains fully available - not just tag ids - when the server is unreachable.
        void this.dataOfflineService.syncTagsFromServer(tags).catch((cacheErr) =>
          console.warn('[DataService] Failed to cache tags to IndexedDB:', cacheErr),
        );
        return {
          ...resp,
          data: tags,
        } as ApiResponse<Tag[]>;
      }),
      catchError((err: any) =>
        // Offline-first fallback: while offline (or on any request failure), use the
        // locally cached tag list (real names/colors, synced the last time the server
        // was reachable) rather than losing the tag names entirely.
        from(this.dataOfflineService.getAllTagsLocal()).pipe(
          switchMap((cachedTags) => {
            if (cachedTags.length > 0) {
              console.warn('[DataService] Tag request failed; using locally cached tags instead:', err);
              this.allTags = cachedTags;
              return of({ status: 'success', data: cachedTags } as ApiResponse<Tag[]>);
            }

            // Last resort (e.g. very first run offline, before tags were ever cached):
            // derive a usable tag list from locally cached notes instead of returning
            // an empty list.
            return from(this.dataOfflineService.getAllNotes()).pipe(
              map((notes) => {
                const localTags = this.deriveTagsFromLocalNotes(notes);
                if (localTags.length > 0) {
                  console.warn('[DataService] Tag request failed; derived tags from local notes instead:', err);
                  this.allTags = localTags;
                  return { status: 'success', data: localTags } as ApiResponse<Tag[]>;
                }
                return {
                  status: 'error',
                  error: {
                    code: err?.status ?? 0,
                    message:
                      err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? 'Failed to load tags.',
                  },
                } as ApiResponse<Tag[]>;
              }),
            );
          }),
        ),
      ),
    );
  }

  createTag(payload: TagMutationPayload): Observable<ApiResponse<Tag>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/tags`, this.serializeTagMutationPayload(payload)).pipe(
      map((resp) => {
        const tag = resp.data ? this.normalizeTagFromApi(resp.data) : undefined;
        if (tag) {
          this.upsertTagCache(tag);
        }
        return {
          ...resp,
          data: tag,
        } as ApiResponse<Tag>;
      }),
      catchError((err: any) =>
        of<ApiResponse<Tag>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: this.extractTagApiErrorMessage(err, 'Failed to create tag.'),
          },
        }),
      ),
    );
  }

  updateTag(tagId: string, payload: TagMutationPayload): Observable<ApiResponse<Tag>> {
    return this.http.put<ApiResponse<any>>(
      `${this.baseUrl}/tags/${encodeURIComponent(tagId)}`,
      this.serializeTagMutationPayload(payload),
    ).pipe(
      map((resp) => {
        const tag = resp.data ? this.normalizeTagFromApi(resp.data) : undefined;
        if (tag) {
          this.upsertTagCache(tag);
        }
        return {
          ...resp,
          data: tag,
        } as ApiResponse<Tag>;
      }),
      catchError((err: any) =>
        of<ApiResponse<Tag>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: this.extractTagApiErrorMessage(err, 'Failed to update tag.'),
          },
        }),
      ),
    );
  }

  deleteTag(tagId: string): Observable<ApiResponse<Tag>> {
    return this.http.delete<ApiResponse<any>>(`${this.baseUrl}/tags/${encodeURIComponent(tagId)}`).pipe(
      map((resp) => {
        const tag = resp.data ? this.normalizeTagFromApi(resp.data) : undefined;
        if (tag) {
          this.upsertTagCache(tag);
        } else {
          this.allTags = this.allTags.filter((existingTag) => existingTag._id !== tagId);
        }
        return {
          ...resp,
          data: tag,
        } as ApiResponse<Tag>;
      }),
      catchError((err: any) =>
        of<ApiResponse<Tag>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? 'Failed to delete tag.',
          },
        }),
      ),
    );
  }

  getTagInfo(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/tags/info`).pipe(
      catchError((err: any) =>
        of<ApiResponse<any>>({
          status: 'error',
          error: {
            code: err?.status ?? 0,
            message: err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? 'Failed to load tag info.',
          },
        }),
      ),
    );
  }

  getAllNotesAndCategories(): Observable<
    ApiResponse<{ notes: Note[]; categories: Category[] }>
  > {
    return of({
      status: 'success' as const,
      data: { notes: this.allNotes, categories: this.allCategories },
    });
  }

  getNote(id: string): Observable<ApiResponse<Note | undefined>> {
    return defer(() => from(this.dataOfflineService.getNote(id))).pipe(
      map((note) =>
        note
          ? { status: 'success' as const, data: note }
          : {
              status: 'error' as const,
              error: { code: 404, message: 'Note not found' },
            },
      ),
    );
  }

  /* ============================================================
     STATIC LOOKUPS (used all over UI)
     ============================================================ */

  getAllProjects(): Project[] {
    return [
      { ID: 1, Name: 'Art / Photography' },
      { ID: 2, Name: 'At Home' },
      { ID: 3, Name: 'Career' },
      { ID: 4, Name: 'Work' },
    ];
  }

  getAllStatuses(): string[] {
    return [
      'Cancelled',
      'Completed',
      'Current',
      'Expired',
      'Inbox',
      'N/A',
      'Next Action',
      'Obsolete',
      'Someday',
    ];
  }

  getAllNoteTypes(): string[] {
    return ['To Do', 'Event', 'Journal', 'Knowledgebase'];
  }

  /* ============================================================
     STUBS / LEGACY — preserved for compatibility
     ============================================================ */

//   async syncAllChanges(): Promise<void> {
//     this.logs.append('synch', 'syncAllChanges() called (no-op)');
//   }

  createNote() {
    return this.dataOfflineService.createNote();
  }

  createNoteFromTemplate(t: Note) {
    return this.dataOfflineService.createNoteFromTemplate(t);
  }

  createNoteCopy(note: Note) {
    return this.dataOfflineService.createNoteCopy(note);
  }

  createTemplateFromNote(n: Note, name: string) {
    return this.dataOfflineService.createTemplateFromNote(n, name);
  }




  
  updateNote(n: Note): Observable<ApiResponse<Note>> {
    this.logs.append('synch', `[SAVE] start ${n._id} (has_rev=${!!n._rev})`);

    return defer(() => from(this.dataOfflineService.saveNote(n, false))).pipe(
      map(() => {
        this.logs.append('synch', `[SAVE] local IndexedDB write complete ${n._id}`);
        this.upsertNoteInMemory(n);
        this.emitLocalDataChanged();
        this.logs.append('synch', `[SAVE] local UI refresh emitted ${n._id}`);

        if (!this.isOnline) {
          this.logs.append('synch', `[SAVE] remote skipped (offline) ${n._id}`);
          return {
            status: 'warning' as const,
            data: n,
            error: { code: 0, message: 'Saved locally. Offline; will sync later.' },
          };
        }

        // The write is already durable in IndexedDB at this point — don't block
        // the caller (e.g. the Save button) on the remote round-trip. Push it in
        // the background; if it fails, the note stays dirty and SyncCoordinator's
        // own push cycle (with its per-note retry backoff) will pick it up.
        this.pushNoteInBackground(n);
        return { status: 'success' as const, data: n };
      }),
      catchError((err: any) => {
        this.logs.append('synch', `[SAVE] local write failed ${n._id}: ${err?.message ?? String(err)}`);
        return of<ApiResponse<Note>>({
          status: 'error',
          error: { code: 0, message: err?.message ?? String(err) },
        });
      }),
    );
  }

  /**
   * Lightweight local-only save used by autosave. Writes to IndexedDB and
   * marks the note dirty for the existing background sync cycle, but does
   * NOT push to the remote immediately (unlike updateNote()) — autosave
   * fires every few seconds and must never require server availability or
   * trigger a remote round-trip (and any backend AI/indexing work) more
   * often than the existing SyncCoordinator push cycle already does.
   */
  async autosaveNote(n: Note): Promise<void> {
    await this.dataOfflineService.saveNote(n, false);
    this.upsertNoteInMemory(n);
    this.emitLocalDataChanged();
  }

  /** Autosave Stage 2 — opening-snapshot CRUD. IndexedDB-only, never synced. */
  saveEditSessionSnapshot(snapshot: NoteEditSessionSnapshot): Promise<void> {
    return this.dataOfflineService.saveEditSessionSnapshot(snapshot);
  }

  getEditSessionSnapshot(noteId: string, sessionId: string): Promise<NoteEditSessionSnapshot | undefined> {
    return this.dataOfflineService.getEditSessionSnapshot(noteId, sessionId);
  }

  deleteEditSessionSnapshot(noteId: string, sessionId: string): Promise<void> {
    return this.dataOfflineService.deleteEditSessionSnapshot(noteId, sessionId);
  }

  getAllEditSessionSnapshots(): Promise<NoteEditSessionSnapshot[]> {
    return this.dataOfflineService.getAllEditSessionSnapshots();
  }

  private pushNoteInBackground(n: Note): void {
    const shouldCreate = !n._rev;

    const remoteCall$: Observable<Note> = shouldCreate
      ? (() => {
          this.logs.append('synch', `[SAVE] remote create attempt ${n._id}`);
          return this.dataRemoteService.importNoteFromOffline(n).pipe(
            concatMap((created) => {
              // Backend response is authoritative for persisted fields (for example Size).
              const createdNote: Note = { ...n, ...(created ?? {}) };
              return from(this.dataOfflineService.saveNote(createdNote, true)).pipe(map(() => createdNote));
            }),
          );
        })()
      : (() => {
          this.logs.append('synch', `[SAVE] remote update attempt ${n._id} rev=${n._rev ?? 'n/a'}`);
          return from(this.dataRemoteService.updateNote(n));
        })();

    remoteCall$.subscribe({
      next: (updated) => {
        this.logs.append('synch', `[SAVE] remote push success ${n._id} rev=${updated._rev ?? 'n/a'}`);
        this.upsertNoteInMemory(updated);
        this.emitLocalDataChanged();
        this.triggerNoteIndexRefresh(updated);
        this.logs.append('synch', `[SAVE] local UI refresh emitted ${n._id}`);
      },
      error: (err: any) => {
        // Try to extract backend error details for the log; the note remains
        // dirty in IndexedDB so SyncCoordinator will retry it automatically.
        let message = err?.message ?? 'Remote push failed.';
        if (err?.error) {
          if (typeof err.error === 'object' && err.error.message) {
            message = err.error.message;
          } else if (typeof err.error === 'string') {
            message = err.error;
          }
        }
        this.logs.append('synch', `[SAVE] remote push failed ${n._id}: ${message}`);
        this.snackbarService.showErrorMessage(
          `"${n.Title || 'Note'}" was saved locally but could not sync to the server yet. It will retry automatically.`,
        );
      },
    });
  }

deleteNote(note: Note): Observable<ApiResponse<Note>> {
  // If it never existed remotely, delete locally only.
  if (note.syncStatus === 'new') {
    return defer(() => from(this.dataOfflineService.deleteNote(note._id))).pipe(
      map((): ApiResponse<Note> => ({ status: 'success', data: note } as const)),
      catchError((err: any) =>
        of<ApiResponse<Note>>({
          status: 'error',
          error: { code: 0, message: `Failed to delete locally: ${err?.message ?? String(err)}` },
        }),
      ),
    );
  }

  // Always delete locally first
  return defer(() => from(this.dataOfflineService.deleteNote(note._id))).pipe(
    concatMap(() => {
      if (!this.isOnline) {
        // mark as deleted locally; server deletion later
        return of<ApiResponse<Note>>({
          status: 'warning',
          data: note,
          error: { code: 0, message: 'Deleted locally. Offline; will sync later.' },
        });
      }

      return this.dataRemoteService.deleteNote(note._id, note._rev || '').pipe(
        map((): ApiResponse<Note> => ({ status: 'success', data: note } as const)),
        catchError((err: any) =>
          of<ApiResponse<Note>>({
            status: 'warning',
            data: note,
            error: {
              code: err?.status ?? 0,
              message: `Deleted locally. CouchDB deletion failed: ${err?.message ?? String(err)}`,
            },
          }),
        ),
      );
    }),
    catchError((err: any) =>
      of<ApiResponse<Note>>({
        status: 'error',
        error: { code: 0, message: `Failed to delete locally: ${err?.message ?? String(err)}` },
      }),
    ),
  );
}

bulkSetCategory(
  noteIds: string[],
  categoryId: string,
  updateTimestamp: boolean,
): Observable<ApiResponse<BulkCategoryResult>> {
  if (!noteIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No notes selected.' } });
  }
  if (!categoryId) {
    return of({ status: 'error', error: { code: 400, message: 'No category selected.' } });
  }
  if (!this.isOnline) {
    return of({ status: 'error', error: { code: 0, message: 'Offline. Bulk update requires online.' } });
  }

  return this.dataRemoteService.bulkSetCategory(noteIds, categoryId, updateTimestamp).pipe(
    catchError((err: any) =>
      of<ApiResponse<BulkCategoryResult>>({
        status: 'error',
        error: { code: err?.status ?? 0, message: err?.message ?? 'Bulk category update failed.' },
      }),
    ),
  );
}

bulkSetStatus(
  noteIds: string[],
  status: string,
  updateTimestamp: boolean,
): Observable<ApiResponse<BulkStatusResult>> {
  if (!noteIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No notes selected.' } });
  }
  if (!status) {
    return of({ status: 'error', error: { code: 400, message: 'No status provided.' } });
  }
  if (!this.isOnline) {
    return of({ status: 'error', error: { code: 0, message: 'Offline. Bulk update requires online.' } });
  }

  return this.dataRemoteService.bulkSetStatus(noteIds, status, updateTimestamp).pipe(
    catchError((err: any) =>
      of<ApiResponse<BulkStatusResult>>({
        status: 'error',
        error: { code: err?.status ?? 0, message: err?.message ?? 'Bulk status update failed.' },
      }),
    ),
  );
}


deleteNotesOffline(noteIds: string[]): Promise<void> {
  return this.dataOfflineService.deleteNotes(noteIds);
}


bulkDelete(noteIds: string[]): Observable<ApiResponse<BulkDeleteResult>> {
  if (!noteIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No notes selected.' } });
  }
  if (!this.isOnline) {
    return of({ status: 'error', error: { code: 0, message: 'Offline. Bulk delete requires online.' } });
  }

  return this.dataRemoteService.bulkDelete(noteIds).pipe(
    concatMap((resp) => {
      if (resp.status !== 'success') return of(resp);

      // Keep IndexedDB in sync best-effort
      return from(this.deleteNotesOffline(noteIds)).pipe(map(() => resp));
    }),
    catchError((err: any) =>
      of<ApiResponse<BulkDeleteResult>>({
        status: 'error',
        error: { code: err?.status ?? 0, message: err?.message ?? 'Bulk delete failed.' },
      }),
    ),
  );
}

bulkAddTags(noteIds: string[], tagIds: string[]): Observable<ApiResponse<BulkTagAddResult>> {
  if (!noteIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No notes selected.' } });
  }
  if (!tagIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No tags selected.' } });
  }
  if (!this.isOnline) {
    return of({ status: 'error', error: { code: 0, message: 'Offline. Bulk tag update requires online.' } });
  }

  return this.dataRemoteService.bulkAddTags(noteIds, tagIds).pipe(
    catchError((err: any) =>
      of<ApiResponse<BulkTagAddResult>>({
        status: 'error',
        error: { code: err?.status ?? 0, message: err?.message ?? 'Bulk add tags failed.' },
      }),
    ),
  );
}

bulkRemoveTags(
  noteIds: string[],
  tagIds: string[],
  removeAll: boolean,
): Observable<ApiResponse<BulkTagRemoveResult>> {
  if (!noteIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No notes selected.' } });
  }
  if (!removeAll && !tagIds?.length) {
    return of({ status: 'error', error: { code: 400, message: 'No tags selected.' } });
  }
  if (!this.isOnline) {
    return of({ status: 'error', error: { code: 0, message: 'Offline. Bulk tag update requires online.' } });
  }

  return this.dataRemoteService.bulkRemoveTags(noteIds, tagIds, removeAll).pipe(
    catchError((err: any) =>
      of<ApiResponse<BulkTagRemoveResult>>({
        status: 'error',
        error: { code: err?.status ?? 0, message: err?.message ?? 'Bulk remove tags failed.' },
      }),
    ),
  );
}


  searchNotes(q: string) {
    return from(this.dataOfflineService.searchNotes(q));
  }

  generateKeywords(payload: any) {
    return this.http.post<ApiResponse<any>>('/api/ai/generate-keywords', payload);
  }

  suggestTags(payload: { note_id: string }): Observable<ApiResponse<SuggestTagsResponse>> {
    return this.http.post<ApiResponse<SuggestTagsResponse>>('/api/ai/suggest-tags', payload);
  }

  regenerateAiSummary(noteId: string): Observable<ApiResponse<AiSummaryRegenerateResult>> {
    const encoded = encodeURIComponent(String(noteId ?? '').trim());
    return this.http.post<ApiResponse<AiSummaryRegenerateResult>>(`/api/notes/${encoded}/summary/regenerate`, {}).pipe(
      catchError((err: any) =>
        of({
          status: 'error' as const,
          error: { code: err?.status ?? 0, message: err?.error?.message ?? err?.message ?? 'AI summary request failed.' },
        }),
      ),
    );
  }

  getAiSummaryStatus(noteId: string): Observable<ApiResponse<AiSummaryStatusResult>> {
    const encoded = encodeURIComponent(String(noteId ?? '').trim());
    return this.http.get<ApiResponse<AiSummaryStatusResult>>(`/api/notes/${encoded}/summary/status`).pipe(
      catchError((err: any) =>
        of({
          status: 'error' as const,
          error: { code: err?.status ?? 0, message: err?.error?.message ?? err?.message ?? 'Failed to check AI summary status.' },
        }),
      ),
    );
  }

  findSimilarNotes(noteId: string): Observable<ApiResponse<SimilarNoteResult[]>> {
    const rawId = String(noteId ?? '').trim();
    const apiNoteId = rawId.startsWith('note:')
      ? rawId.slice('note:'.length)
      : rawId.startsWith('template:')
        ? rawId.slice('template:'.length)
        : rawId;
    const encoded = encodeURIComponent(apiNoteId);
    return this.http.get<ApiResponse<SimilarNoteResult[]>>(`${this.baseUrl}/notes/${encoded}/similar`);
  }

  putNoteToCouchDB(note: Note) {
    return this.dataRemoteService.putNoteToCouchDB(note);
  }

  cleanupCouchDBConflicts(id: string) {
    return this.dataRemoteService.cleanupCouchDBConflicts(id);
  }

  saveNoteOffline(note: Note, overwrite: boolean) {
    return this.dataOfflineService.saveNote(note, overwrite).then(() => {
      this.upsertNoteInMemory(note);
      this.emitLocalDataChanged();
    });
  }

  getTemplates() {
    return this.dataRemoteService.getTemplates();
  }

  /**
   * Get templates from cache (instant access)
   * Returns cached templates if available, otherwise empty array
   */
  getTemplatesFromCache(): Note[] {
    return this.allTemplates;
  }

  /**
   * Check if templates have been loaded
   */
  areTemplatesLoaded(): boolean {
    return this.templatesLoadedSubject.value;
  }

  /**
   * Load templates into cache for instant access
   * Called at startup and after template modifications
   *
   * Offline-first: template:* docs are kept current in IndexedDB by the same
   * sync pipeline that syncs notes, so seed the cache from there first (works
   * offline), then best-effort refresh from the server. A failed/offline
   * network refresh must never reduce the cache to an empty list.
   */
  private loadTemplatesIntoCache(): void {
    this.dataOfflineService
      .getAllNotes()
      .then((notes) => {
        const localTemplates = (notes ?? []).filter((n) => n.type === 'template');
        this.allTemplates = localTemplates;
        this.templatesLoadedSubject.next(true);
        console.log(`[DataService] Loaded ${localTemplates.length} template(s) from IndexedDB`);
      })
      .catch((err) => {
        console.error('[DataService] Failed to load templates from IndexedDB:', err);
        this.templatesLoadedSubject.next(true);
      })
      .finally(() => {
        this.dataRemoteService
          .getTemplates()
          .pipe(
            catchError((err) => {
              console.warn('[DataService] Template network refresh failed, keeping cached templates:', err);
              return of(null);
            }),
          )
          .subscribe((templates) => {
            if (templates) {
              this.allTemplates = templates;
              console.log(`[DataService] Refreshed ${templates.length} template(s) from server`);
            }
          });
      });
  }

  /**
   * Refresh template cache
   * Call this after creating, updating, or deleting templates
   */
  refreshTemplateCache(): void {
    this.loadTemplatesIntoCache();
  }

  private normalizeTagFromApi(rawTag: any): Tag {
    const rawColor = rawTag?.color ?? rawTag?.Color ?? rawTag?.tagColor ?? rawTag?.tag_color;
    const rawDescription = rawTag?.description ?? rawTag?.Description;
    return {
      _id: String(rawTag?._id ?? ''),
      _rev: rawTag?._rev ? String(rawTag._rev) : undefined,
      type: 'tag',
      name: String(rawTag?.name ?? ''),
      description: typeof rawDescription === 'string' ? rawDescription : undefined,
      tagType: String(rawTag?.tagType ?? rawTag?.tag_type ?? 'general'),
      slug: String(rawTag?.slug ?? ''),
      color: rawColor ? String(rawColor) : undefined,
      icon: rawTag?.icon ? String(rawTag.icon) : undefined,
      parentTagId: rawTag?.parentTagId ?? rawTag?.parent_tag_id ?? null,
      sortOrder: Number.isFinite(rawTag?.sortOrder) ? Number(rawTag.sortOrder) : Number(rawTag?.sort_order ?? 0),
      isActive: typeof rawTag?.isActive === 'boolean' ? rawTag.isActive : rawTag?.is_active !== false,
    };
  }

  /**
   * Offline-first fallback for getAllTags(): derives a unique, sorted tag list from
   * the Tags field of locally cached notes. Note.Tags entries may be a plain tag-id
   * string (no further detail available offline) or an embedded Tag object.
   */
  private deriveTagsFromLocalNotes(notes: Note[]): Tag[] {
    const byId = new Map<string, Tag>();

    for (const note of notes ?? []) {
      const noteTags = Array.isArray(note.Tags) ? note.Tags : [];
      for (const entry of noteTags) {
        if (!entry) continue;

        if (typeof entry === 'string') {
          const id = entry.trim();
          if (!id || byId.has(id)) continue;
          byId.set(id, {
            _id: id,
            type: 'tag',
            name: id,
            tagType: 'general',
            slug: id,
            parentTagId: null,
            isActive: true,
          });
          continue;
        }

        const id = String(entry._id ?? '').trim();
        if (!id) continue;
        // Prefer a fully-formed embedded Tag object over a previously-seen id-only stub.
        if (!byId.has(id) || (byId.get(id)?.name === byId.get(id)?._id && entry.name)) {
          byId.set(id, this.normalizeTagFromApi(entry));
        }
      }
    }

    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  private serializeTagMutationPayload(payload: TagMutationPayload): Record<string, unknown> {
    const body: Record<string, unknown> = {
      name: payload.name,
      description: payload.description,
      tagType: payload.tagType,
      slug: payload.slug,
      color: payload.color,
      icon: payload.icon,
      isActive: payload.isActive,
    };

    if (payload.sortOrder !== undefined && payload.sortOrder !== null) {
      body['sort_order'] = payload.sortOrder;
    }

    if (payload.parentTagId !== undefined) {
      body['parent_tag_id'] = payload.parentTagId;
    }

    return body;
  }

  private extractTagApiErrorMessage(err: any, fallback: string): string {
    const fieldErrors = err?.error?.errors?.json;
    if (fieldErrors && typeof fieldErrors === 'object') {
      const details = Object.entries(fieldErrors)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : String(messages)}`)
        .join(' | ');
      if (details) {
        return details;
      }
    }

    return err?.error?.error?.message ?? err?.error?.message ?? err?.message ?? fallback;
  }

  private upsertTagCache(tag: Tag): void {
    const index = this.allTags.findIndex((existingTag) => existingTag._id === tag._id);
    if (index === -1) {
      this.allTags = [...this.allTags, tag].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      return;
    }

    const next = [...this.allTags];
    next[index] = tag;
    this.allTags = next.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }
}
